import { FormGroup } from '@angular/forms';
import { IResponse } from '@howdeniberia/core-front';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ICalculatorBaseRequest, IResponseOutputDto } from 'src/app/core/models/api/sane-project/arpa-model';
import { toBooleanStrict } from 'src/app/shared/utils';
import { ProjectViewModel } from '../../models/project.viewmodel';
import { CalculatorProductViewModel } from '../models/calculator-product.viewmodel';

interface IEditCalcFields {
  projectPaymentType: number | string;
  projectClientProfessionCode: number | string;
  projectClientId: number | string;
  projectEffectiveDate: Date | string;
  projectClientBirthDate: Date | string;
  projectActivity: string;
}

export abstract class CalculatorMapper {
  public static mapCommonFields(project: ProjectViewModel): ICalculatorBaseRequest {
    const e = project.edit as unknown as IEditCalcFields;
    return {
      paymentType: Number(e.projectPaymentType) || 0,
      professionCode: Number(e.projectClientProfessionCode) || 0,
      clientId: Number(e.projectClientId) || 0,
      effectiveDate: (e.projectEffectiveDate as Date) ?? new Date(),
      birthDate: (e.projectClientBirthDate as Date) ?? new Date(),
      // El backend usa "regime" en varios POST (p.ej. IT/Accidentes AXA),
      // así que lo mandamos SIEMPRE en el payload común para todas las tarificaciones.
      // En el front este valor está en "projectActivity".
      regime: e.projectActivity
    };
  }

  public static buildCalculateRequest(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    numericKeys: string[],
    passThroughKeys: string[] = [],
    keyMap: Record<string, string> = {},
    booleanKeys: string[] = []
  ): Record<string, any> {
    const p: Record<string, any> = { ...this.mapCommonFields(project) };

    const read = (k: string): any => {
      if (vm.form instanceof FormGroup && vm.form.contains(k)) {
        return vm.form.get(k)!.value;
      }
      const edit = project?.edit as unknown as Record<string, any> | undefined;
      if (edit && Object.prototype.hasOwnProperty.call(edit, k)) {
        return edit[k];
      }
      if (typeof (vm as any).getValue === 'function') {
        return (vm as any).getValue(k);
      }
      return (vm as any)[k];
    };

    [...numericKeys, ...booleanKeys, ...passThroughKeys].forEach(k => {
      const prop = keyMap[k] ?? k;
      const val = read(k);
      if (numericKeys.includes(k)) {
        p[prop] = Number(val) || 0;
      } else if (booleanKeys.includes(k)) {
        p[prop] = toBooleanStrict(val);
      } else {
        p[prop] = val;
      }
    });

    return p;
  }

  private static applyResponse<T>(resp: IResponse<T>, vm: CalculatorProductViewModel) {
    const raw: any = (resp.result as any)?.responseOutputDto ?? resp.result;
    if (raw) {
      vm.fromResponse(raw as IResponseOutputDto);
    }
  }

  public static executeCalculation<T>(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<T>>,
    numericKeys: string[],
    passThroughKeys: string[] = [],
    keyMap: Record<string, string> = {},
    booleanKeys: string[] = []
  ): Observable<IResponse<T>> {
    const req = this.buildCalculateRequest(project, vm, numericKeys, passThroughKeys, keyMap, booleanKeys);
    return apiCall(req).pipe(tap(r => this.applyResponse(r, vm)));
  }

  private static lifeKeys = {
    allianz: {
      keys: ['vAllianzCapitalDeath', 'vAllianzCapitalIptp', 'vAllianzCapitalIap'],
      map: {
        vAllianzCapitalDeath: 'capitalDeath',
        vAllianzCapitalIptp: 'capitalIptp',
        vAllianzCapitalIap: 'capitalIap'
      }
    },
    axa: {
      keys: [
        'vAxaCapitalDeath',
        'vAxaCapitalIptp',
        'vAxaCapitalIap',
        'vAxaCapitalSevereIllness',
        'projectLifeMotorbike',
        'projectSport',
        'projectLifeScaffoldRoof',
        'projectLifeEspecialRisks',
        'projectLifeDrivingForce'
      ],
      map: {
        vAxaCapitalDeath: 'capitalDeath',
        vAxaCapitalIptp: 'capitalIptp',
        vAxaCapitalIap: 'capitalIap',
        vAxaCapitalSevereIllness: 'capitalSevereIllness',
        projectLifeMotorbike: 'motorcycle',
        projectSport: 'sport',
        projectLifeScaffoldRoof: 'scaffolding',
        projectLifeEspecialRisks: 'risks',
        projectLifeDrivingForce: 'motor'
      }
    },
    staLucia: {
      keys: ['vStaLuciaCapitalDeath', 'vStaLuciaCapitalIptp', 'vStaLuciaCapitalIap'],
      map: {
        vStaLuciaCapitalDeath: 'capitalDeath',
        vStaLuciaCapitalIptp: 'capitalIptp',
        vStaLuciaCapitalIap: 'capitalIap'
      }
    },
    surne: {
      keys: ['vSurneCapitalDeath', 'vSurneCapitalIptp', 'vSurneCapitalIap'],
      map: {
        vSurneCapitalDeath: 'capitalDeath',
        vSurneCapitalIptp: 'capitalIptp',
        vSurneCapitalIap: 'capitalIap'
      }
    },
    surneLifePro: {
      keys: ['vSurneLifeProCapitalDeath', 'vSurneLifeProCapitalIptp', 'vSurneLifeProCapitalIap'],
      map: {
        vSurneLifeProCapitalDeath: 'capitalDeath',
        vSurneLifeProCapitalIptp: 'capitalIptp',
        vSurneLifeProCapitalIap: 'capitalIap'
      }
    },
    helvetia: {
      keys: ['vHelvetiaCapitalDeath', 'vHelvetiaCapitalIptp', 'vHelvetiaCapitalIap'],
      map: {
        vHelvetiaCapitalDeath: 'capitalDeath',
        vHelvetiaCapitalIptp: 'capitalIptp',
        vHelvetiaCapitalIap: 'capitalIap'
      }
    },
    prebal: {
      keys: ['vPrebalCapitalDeath', 'vPrebalCapitalIptp', 'vPrebalCapitalIap'],
      map: {
        vPrebalCapitalDeath: 'capitalDeath',
        vPrebalCapitalIptp: 'capitalIptp',
        vPrebalCapitalIap: 'capitalIap'
      }
    },
    prebalInvPro: {
      keys: ['vPrebalInvProCapitalIptp', 'vPrebalInvProCapitalIap'],
      map: {
        vPrebalInvProCapitalIptp: 'capitalIptp',
        vPrebalInvProCapitalIap: 'capitalIap'
      }
    },
    zurich: {
      keys: ['vZurichCapitalDeath', 'vZurichCapitalIptp', 'vZurichCapitalIap'],
      map: {
        vZurichCapitalDeath: 'capitalDeath',
        vZurichCapitalIptp: 'capitalIptp',
        vZurichCapitalIap: 'capitalIap'
      }
    }
  };

  private static accidentKeys = {
    axa: {
      keys: [
        'aAxaCapitalDeath',
        'aAxaCapitalDeathTrafficAccident',
        'aAxaCapitalHeartAttack',
        'aAxaCapitalDisabilityType',
        'aAxaCapitalDisability',
        'aAxaCapitalPermanentDisabilityTrafficAccident',
        'aAxaCapitalMedicalExpensesForAccidents',
        'aAxaCapitalHospitalizationIllnessAccident',
        'aAxaCapitalFamilyAccidentAssistance',
        'aAxaCapitalHIVHepatitisInfection'
      ],
      map: {
        aAxaCapitalDeath: 'capitalDeath',
        aAxaCapitalDeathTrafficAccident: 'capitalDeathTrafficAccident',
        aAxaCapitalHeartAttack: 'capitalHeartAttack',
        aAxaCapitalDisabilityType: 'capitalDisabilityType',
        aAxaCapitalDisability: 'capitalDisability',
        aAxaCapitalPermanentDisabilityTrafficAccident: 'capitalPermanentDisabilityTrafficAccident',
        aAxaCapitalMedicalExpensesForAccidents: 'capitalMedicalExpensesForAccidents',
        aAxaCapitalHospitalizationIllnessAccident: 'capitalHospitalizationIllnessAccident',
        aAxaCapitalFamilyAccidentAssistance: 'capitalFamilyAccidentAssistance',
        aAxaCapitalHIVHepatitisInfection: 'capitalHIVHepatitisInfection'
      }
    },
    surne: {
      keys: [
        'aSurneCapitalDeath',
        'aSurneCapitalHeartAttack',
        'aSurneCapitalAbsoluteDisabilityType',
        'aSurneCapitalAbsoluteDisability',
        'aSurneCapitalTotalPermanentDisability',
        'aSurneCapitalHealthDisabilityType',
        'aSurneRateIdHIVHepatitisInfection'
      ],
      map: {
        aSurneCapitalDeath: 'capitalDeath',
        aSurneCapitalHeartAttack: 'capitalHeartAttack',
        aSurneCapitalAbsoluteDisabilityType: 'capitalAbsoluteDisabilityType',
        aSurneCapitalAbsoluteDisability: 'capitalAbsoluteDisability',
        aSurneCapitalTotalPermanentDisability: 'capitalTotalPermanentDisability',
        aSurneCapitalHealthDisabilityType: 'capitalHealthDisabilityType',
        aSurneRateIdHIVHepatitisInfection: 'rateIdHIVHepatitisInfection'
      }
    },
    prebal: {
      keys: [
        'aPrebalCapitalDeath',
        'aPrebalCapitalDeathTrafficAccident',
        'aPrebalCapitalDisabilityType',
        'aPrebalCapitalDisability',
        'aPrebalHasSelectedCapitalDisability',
        'capitalHealthCareAccidents',
        'aPrebalRateIdHIVHepatitisInfection'
      ],
      map: {
        aPrebalCapitalDeath: 'capitalDeath',
        aPrebalCapitalDeathTrafficAccident: 'capitalDeathTrafficAccident',
        aPrebalCapitalDisabilityType: 'capitalDisabilityType',
        aPrebalCapitalDisability: 'capitalDisability',
        aPrebalHasSelectedCapitalDisability: 'hasSelectedCapitalDisability',
        capitalHealthCareAccidents: 'capitalHealthCareAccidents',
        aPrebalRateIdHIVHepatitisInfection: 'rateIdHIVHepatitisInfection'
      }
    }
  };

  public static calcLifeAllianz(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.allianz;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeAxa(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.axa;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeStaLucia(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.staLucia;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeSurne(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.surne;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeSurneLifePro(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.surneLifePro;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeHelvetia(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.helvetia;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifePrebal(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.prebal;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifePrebalInvPro(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.prebalInvPro;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcLifeZurich(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.lifeKeys.zurich;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcDependencyAxa(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['depAxaSevereDependency', 'depAxaGreatDependency'],
      [],
      {
        depAxaSevereDependency: 'severeDependency',
        depAxaGreatDependency: 'greatDependency'
      }
    );
  }

  public static calcDependencySurne(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['depSurneSevereDependency', 'depSurneGreatDependency', 'depSurneCapitalIncome'],
      [],
      {
        depSurneSevereDependency: 'severeDependency',
        depSurneGreatDependency: 'greatDependency',
        depSurneCapitalIncome: 'capitalIncome'
      }
    );
  }

  public static calcDecAura(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      [],
      [],
      { decAuraSelectChargeable: 'chargeable' },
      ['decAuraSelectChargeable']
    );
  }

  public static calcItPmElite(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['itPmEliteSubsidy', 'itPmEliteFranchise', 'itPmEliteDuration', 'itPmEliteHospitalization'],
      [],
      {
        itPmEliteSubsidy: 'subsidy',
        itPmEliteFranchise: 'franchise',
        itPmEliteDuration: 'duration',
        itPmEliteHospitalization: 'hospitalization'
      }
    );
  }

  public static calcItPmCoeg(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['itPmCoegSubsidy', 'itPmCoegFranchise', 'itPmCoegDuration'],
      [],
      {
        itPmCoegSubsidy: 'subsidy',
        itPmCoegFranchise: 'franchise',
        itPmCoegDuration: 'duration'
      }
    );
  }

  public static calcItSurnePlus(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['itSurnePlusSubsidy', 'itSurnePlusFranchise', 'itSurnePlusDuration', 'itSurnePlusHospitalization'],
      [],
      {
        itSurnePlusSubsidy: 'subsidy',
        itSurnePlusFranchise: 'franchise',
        itSurnePlusDuration: 'duration',
        itSurnePlusHospitalization: 'hospitalization'
      }
    );
  }

  public static calcItSurneVip(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['itSurneVipSubsidy', 'itSurneVipFranchise', 'itSurneVipDuration'],
      [],
      {
        itSurneVipSubsidy: 'subsidy',
        itSurneVipFranchise: 'franchise',
        itSurneVipDuration: 'duration'
      }
    );
  }

  public static calcItPrebal(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    return this.executeCalculation(
      project,
      vm,
      apiCall,
      ['itPrebalSubsidy', 'itPrebalFranchise', 'itPrebalDuration', 'itPrebalHospitalization'],
      ['itPrebalHasLife'],
      {
        itPrebalSubsidy: 'subsidy',
        itPrebalFranchise: 'franchise',
        itPrebalDuration: 'duration',
        itPrebalHospitalization: 'hospitalization',
        itPrebalHasLife: 'hasLife'
      },
      ['itPrebalHasLife']
    );
  }

  public static calcAccidentsAxa(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.accidentKeys.axa;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcAccidentsSurne(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.accidentKeys.surne;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }

  public static calcAccidentsPrebal(
    project: ProjectViewModel,
    vm: CalculatorProductViewModel,
    apiCall: (req: Record<string, any>) => Observable<IResponse<any>>
  ) {
    const cfg = this.accidentKeys.prebal;
    return this.executeCalculation(project, vm, apiCall, cfg.keys, [], cfg.map);
  }
}
